(() => {
  if (customElements.get('kleengent-centered-carousel')) return;
  class KleengentCenteredCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('[data-kgcc-track]');
      this.viewport = this.querySelector('[data-kgcc-viewport]');
      this.originals = [...this.querySelectorAll('[data-kgcc-slide]')];
      this.count = this.originals.length;
      if (!this.track || !this.viewport || !this.count) return;
      this.controller = new AbortController();
      const listen = (target, event, handler, options = {}) => target.addEventListener(event, handler, { ...options, signal: this.controller.signal });
      const clone = (slide) => {
        const copy = slide.cloneNode(true);
        copy.dataset.kgccClone = 'true';
        copy.removeAttribute('data-shopify-editor-block');
        copy.setAttribute('aria-hidden', 'true');
        copy.setAttribute('inert', '');
        copy.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
        copy.querySelectorAll('video').forEach(video => { video.autoplay = false; video.pause(); });
        return copy;
      };
      if (this.count > 1) {
        this.originals.forEach(slide => this.track.append(clone(slide)));
        const prefix = document.createDocumentFragment();
        this.originals.forEach(slide => prefix.append(clone(slide)));
        this.track.prepend(prefix);
      }
      this.slides = [...this.track.children];
      this.index = this.count > 1 ? this.count : 0;
      this.classList.toggle('is-single', this.count === 1);
      this.hovered = false;
      this.keyboardFocus = false;
      this.editorSelected = false;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.duration = Number(this.dataset.transition || 700);
      this.interval = Number(this.dataset.interval || 5) * 1000;
      listen(this.querySelector('[data-kgcc-previous]'), 'click', () => this.move(-1));
      listen(this.querySelector('[data-kgcc-next]'), 'click', () => this.move(1));
      listen(this, 'mouseenter', () => { this.hovered = true; this.schedule(); });
      listen(this, 'mouseleave', () => { this.hovered = false; this.schedule(); });
      listen(this, 'focusin', event => { this.keyboardFocus = event.target.matches(':focus-visible'); this.schedule(); });
      listen(this, 'focusout', () => { this.keyboardFocus = false; this.schedule(); });
      listen(this.viewport, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.move(event.key === 'ArrowRight' ? 1 : -1);
      });
      listen(this, 'touchstart', event => { this.touchX = event.touches[0].clientX; this.touching = true; this.schedule(); }, { passive: true });
      listen(this, 'touchend', event => {
        const delta = event.changedTouches[0].clientX - this.touchX;
        this.touching = false;
        if (Math.abs(delta) > 40) this.move(delta < 0 ? 1 : -1);
        this.schedule();
      }, { passive: true });
      listen(this, 'touchcancel', () => { this.touching = false; this.schedule(); });
      listen(document, 'visibilitychange', () => this.schedule());
      listen(document, 'shopify:block:select', event => {
        const slide = this.originals.find(node => node === event.target || node.contains(event.target));
        if (!slide) return;
        clearTimeout(this.transitionTimer);
        this.moving = false;
        this.editorSelected = true;
        this.index = this.originals.indexOf(slide) + (this.count > 1 ? this.count : 0);
        this.position(false);
        this.schedule();
      });
      listen(document, 'shopify:block:deselect', () => { this.editorSelected = false; this.schedule(); });
      this.resizeObserver = new ResizeObserver(() => this.position(false));
      this.resizeObserver.observe(this.viewport);
      this.position(false);
      this.schedule();
    }
    position(animate) {
      const active = this.slides[this.index];
      if (!active) return;
      const offset = this.viewport.getBoundingClientRect().width / 2 - (active.offsetLeft + active.offsetWidth / 2);
      this.track.style.transitionDuration = animate && !this.reducedMotion.matches ? `${this.duration}ms` : '0ms';
      this.track.style.transform = `translateX(${offset}px)`;
      this.slides.forEach((slide, index) => {
        const selected = index === this.index;
        slide.classList.toggle('is-active', selected);
        if (!slide.hasAttribute('data-kgcc-clone')) {
          slide.setAttribute('aria-hidden', String(!selected));
          slide.toggleAttribute('inert', !selected);
        }
        slide.querySelectorAll('video').forEach(video => {
          if (!selected) video.pause();
          else if (video.autoplay) video.play().catch(() => {});
        });
      });
    }
    move(direction) {
      if (this.count < 2 || this.moving) return;
      this.moving = true;
      this.index += direction;
      this.position(true);
      this.transitionTimer = setTimeout(() => {
        if (this.index >= this.count * 2) this.index -= this.count;
        if (this.index < this.count) this.index += this.count;
        this.position(false);
        this.moving = false;
      }, this.reducedMotion.matches ? 0 : this.duration + 20);
    }
    schedule() {
      clearInterval(this.autoplayTimer);
      if (this.dataset.autoplay === 'false' || this.count < 2 || document.hidden || this.hovered || this.keyboardFocus || this.touching || this.editorSelected) return;
      this.autoplayTimer = setInterval(() => this.move(1), this.interval);
    }
    disconnectedCallback() {
      clearInterval(this.autoplayTimer);
      clearTimeout(this.transitionTimer);
      this.resizeObserver?.disconnect();
      this.controller?.abort();
      this.querySelectorAll('[data-kgcc-clone]').forEach(node => node.remove());
      this.controller = null;
    }
  }
  customElements.define('kleengent-centered-carousel', KleengentCenteredCarousel);
})();
